import { tallyRows } from "./tally";
import type { Activity, Results } from "./types";

/**
 * ═══ BEAUCOUP DE RÉPONSES SUR LE MUR ═══
 *
 * La carte de la salle est taillée pour quatre réponses, l'une sous l'autre, en lettres
 * lisibles à vingt mètres. Au-delà, elles sortaient de l'écran — par le bas ET par le
 * haut, puisque la scène centre son contenu : un vidéoprojecteur ne défile pas, et la
 * salle ne voyait ni la première ni les dernières (tâche #326).
 *
 * Deux leviers. Les réponses se rangent en COLONNES — la carte est large, c'est sa
 * hauteur qui manque —, et quand ça ne suffit plus, tout rétrécit dans la même
 * proportion : le texte, les marges, les écarts. Entre une, deux et trois colonnes, on
 * garde celle qui laisse le texte le plus grand.
 *
 * ⚠️ LES RÉSULTATS RESTENT SUR UNE COLONNE TANT QUE POSSIBLE. Des barres se comparent
 * par leur longueur, à partir d'un même bord : réparties sur deux colonnes, la plus
 * longue de gauche et la plus longue de droite se comparent mal. Ils rétrécissent
 * d'abord, et ne passent sur deux colonnes que lorsqu'une seule ne tient plus du tout.
 *
 * ⚠️ LE NOMBRE DE RÉPONSES NE SUFFIT PAS, IL FAUT LEUR LONGUEUR. Douze « Réponse 1 »
 * tiennent sur trois colonnes ; douze « Plutôt d'accord avec la proposition » y passent
 * sur deux ou trois lignes chacune, et la carte redéborde. La règle estime donc les
 * lignes de chaque libellé, mot à mot.
 *
 * ═══ ET SUR L'ÉCRAN QU'ON A VRAIMENT (tâche #334) ═══
 *
 * ⚠️ CALCULÉ POUR LA FENÊTRE, PAS POUR UN MUR SUPPOSÉ. La première version faisait le
 * calcul une fois pour toutes, en `vmin`, sur un projecteur paysage 16/10 : la même
 * activité donnait la même grille partout. Deux écrans la démentaient.
 * - EN PORTRAIT (une borne verticale, une fenêtre posée à côté de la régie, une
 *   tablette), la carte n'a plus 117 `vmin` de large mais 90 : vingt réponses y
 *   passaient sur trois colonnes où « Réponse 1 » se coupait en deux lignes, sous un
 *   grand vide — la hauteur, cette fois, ne manquait pas.
 * - SUR UN PETIT ÉCRAN (640 × 480), les `clamp` de la feuille s'arrêtent à leur
 *   plancher en pixels : un texte de 18 px quand la règle en comptait 16, des marges de
 *   24 px pour 19. Tout grossissait au-delà du calcul, et la carte sortait de l'écran
 *   par le haut et par le bas.
 * La règle reçoit donc la taille de la fenêtre, et refait le calcul de la feuille en
 * pixels, bornes des `clamp` comprises. Elle reste CALCULÉE, pas mesurée dans la page :
 * testable ici, sans navigateur. `Activity` la relance quand la fenêtre change.
 *
 * Les tailles ci-dessous sont celles de `live.css` (`.page.scene`, `.scene__mod`), en
 * `[plancher px, vmin, plafond px]` ; celles qui ne s'y lisent pas ont été mesurées dans
 * Chrome.
 */

export type RoomLayout = {
  /** Le nombre de colonnes : jusqu'à trois pour les réponses, deux pour les résultats. */
  columns: number;
  /** Le facteur appliqué à toutes les tailles de la liste, entre `MIN_SCALE` et 1. */
  scale: number;
};

/**
 * La fenêtre de l'écran de salle, en pixels CSS (`innerWidth` × `innerHeight`) — ou,
 * quand il porte des bandeaux, la place qu'ils lui laissent (`roomBox.ts`) : les unités
 * de la feuille sont alors celles de ce conteneur, et le calcul les suit.
 */
export type Viewport = { width: number; height: number };

/** Un `clamp(plancher, vmin, plafond)` de la feuille. */
type Size = readonly [min: number, vmin: number, max: number];

/** La marge colorée de la page (`.page.scene`), sur chaque bord. */
const PAGE: Size = [20, 4, 72];
/** La carte s'arrête à 1500 px (`.scene__mod`), rembourrée sur chaque bord. */
const CARD_MAX = 1500;
const CARD_PAD: Size = [24, 4, 68];
/** L'écart entre les blocs de la carte (`.scene__mod .mod`, `gap`). */
const GAP: Size = [16, 2.4, 40];
/** L'interligne du texte courant (`base.css`). */
const LINE = 1.55;

/** La question : interligne 1,1, 22 `ch` de large au plus (un `0` = 0,65 `em`). */
const TITLE: Size = [26, 6.4, 88];
const TITLE_LINE = 1.1;
const TITLE_EMS = 22 * 0.65;
/** Le texte sous la question, et le décompte sous les résultats. */
const HINT: Size = [14, 2.4, 34];
/**
 * La consigne du vote par SMS : « Votez par SMS » et son numéro en 1,4 `em`, puis la
 * phrase, qui passe sur deux lignes dans une carte étroite. Comptée sur sa version
 * la plus longue, en français, la langue de référence.
 */
const SMS: Size = [16, 2.8, 40];
const SMS_NUMBER_EM = 1.4;
const SMS_SENTENCE = "Envoyez les numéros de vos réponses, 9 au plus.";

/** Une réponse (`.scene__mod .qz button`), et sa bordure de 1,5 px de chaque côté. */
const CHOICE_FONT: Size = [18, 3.3, 48];
const CHOICE_PAD_Y: Size = [12, 2.2, 34];
const CHOICE_PAD_X: Size = [16, 3, 44];
const CHOICE_GAP: Size = [10, 1.6, 26];
const CHOICE_BORDER = 3;
/** ⚠️ CHAQUE COLONNE S'ARRÊTE À 1100 px (`.scene .qz`). */
const COLUMN_MAX = 1100;
/**
 * Au-delà de trois, une colonne ne tient plus un libellé de quelques mots sans le
 * couper à chaque mot.
 */
const MAX_COLUMNS = 3;

/**
 * Une ligne de résultat, jamais plus basse que son plancher (`.poll__r > *`) — c'est
 * lui qui décide sur un petit écran, où 18 px × 1,55 n'en font que 28. Le libellé
 * tient au plus 34 % de la carte.
 */
const RESULT_FONT: Size = [18, 3.4, 48];
const RESULT_ROW: Size = [32, 5, 72];
const RESULT_GAP: Size = [12, 2, 34];
const RESULT_LABEL = 0.34;
/** La coche de la bonne réponse, réservée devant chaque libellé : 1 `em`. */
const CHECK_EM = 1;
/** Ce qui sépare les deux colonnes de résultats, devant chaque libellé (`live.css`). */
const SPLIT_EM = 1.2;
/** Le camembert, au-dessus des barres. */
const PIE: Size = [120, 20, 300];
/**
 * Le numéro du vote par SMS devant un libellé : une pastille de 1,5 `em` qui s'élargit
 * à 1,8 dès le numéro 10, et 0,5 de marge. Compté à 1,5, « Réponse 10 » passait sur
 * deux lignes que la règle n'avait pas prévues, et vingt résultats débordaient.
 */
const NUMBER_EM = 2.4;

/**
 * ⚠️ UN PLANCHER, parce qu'un texte illisible n'est pas un texte affiché. Quand même la
 * moitié de la taille ne tient pas, c'est l'activité qui ne tient plus sur un mur, et
 * la carte déborde — le moins possible — plutôt que de montrer des pattes de mouche.
 */
const MIN_SCALE = 0.5;

export function roomLayout(
  activity: Pick<Activity, "title" | "content" | "choices" | "showResults" | "config">,
  sms: { numbers: boolean; instruction: boolean },
  viewport: Viewport,
  /**
   * ⚠️ LES RÉSULTATS RANGENT LES LIGNES, la plus choisie en tête (`tally.ts`). Sur deux
   * colonnes, une rangée prend la hauteur de sa plus longue réponse : mesurées dans
   * l'ordre de l'activité, deux longues côte à côte à l'écran se compteraient chacune
   * face à une courte, et la carte déborderait de ce que l'estimation n'a pas vu.
   */
  results?: Results | null,
): RoomLayout {
  const labels = activity.choices.map((choice) => choice.label);

  /* La même règle que `clamp()` dans la feuille, sur cette fenêtre-ci. */
  const vmin = Math.min(viewport.width, viewport.height) / 100;
  const px = ([min, v, max]: Size) => Math.min(max, Math.max(min, v * vmin));

  const page = px(PAGE);
  const pad = px(CARD_PAD);
  const gap = px(GAP);
  /** La largeur utile de la carte, la place qu'elle laisse à la liste en hauteur. */
  const width = Math.min(viewport.width - 2 * page, CARD_MAX) - 2 * pad;
  let space = viewport.height - 2 * page - 2 * pad;

  /* Ce qui ne rétrécit pas : la question, son texte, la consigne du SMS. */
  const title = px(TITLE);
  space -=
    linesOf(activity.title || activity.content || "", Math.min(TITLE_EMS, width / title)) *
    title *
    TITLE_LINE;
  const hint = px(HINT);
  if (activity.title && activity.content) {
    space -= gap + linesOf(activity.content, width / hint) * hint * LINE;
  }
  if (sms.instruction) {
    const font = px(SMS);
    space -= gap + (SMS_NUMBER_EM + linesOf(SMS_SENTENCE, width / font)) * font * LINE;
  }
  space -= gap;

  if (activity.showResults) {
    const pie = activity.config.display === "pie" ? px(PIE) : 0;
    const font = px(RESULT_FONT);
    const row = px(RESULT_ROW);
    const between = px(RESULT_GAP);
    space -= gap + hint * LINE + (pie ? gap : 0);
    const shown = results
      ? tallyRows(activity.choices, results).map(({ choice }) => choice.label)
      : labels;
    const height = (columns: number, k: number) => {
      const reserved =
        CHECK_EM + (sms.numbers ? NUMBER_EM : 0) + (columns - 1) * SPLIT_EM;
      const line = (RESULT_LABEL * width) / columns / (font * k) - reserved;
      return (
        rowsOf(
          shown,
          columns,
          line,
          (lines) => Math.max(row, lines * font * LINE) * k,
          between * k,
        ) +
        pie * k
      );
    };
    /**
     * ⚠️ DEUX COLONNES EN DERNIER RECOURS SEULEMENT, quand une seule déborde même au
     * plancher. Essayées plus tôt — dès que la colonne unique passait sous 70 % —, elles
     * tenaient, mais leur libellé n'a plus qu'un sixième de la carte : « Réponse 1 »
     * s'y coupait en deux lignes, moins lisible que la colonne unique un peu plus petite.
     *
     * ⚠️ ET SEULEMENT SI ELLES TIENNENT. Quand ni l'une ni l'autre ne tient au plancher,
     * comparer deux débordements estimés ne départage rien : l'estimation compte large,
     * et un libellé compté sur deux lignes au lieu d'une double la hauteur prévue. Sur
     * 1920 × 1080, douze longs résultats passaient ainsi sur deux colonnes qui sortaient
     * de l'écran, quand la colonne unique, affichée, tenait.
     */
    const single = fitting(1, height, space);
    if (single.overflow === 0) return strip(single);
    const double = fitting(2, height, space);
    return strip(double.overflow === 0 ? double : single);
  }

  const font = px(CHOICE_FONT);
  const padY = px(CHOICE_PAD_Y);
  const padX = px(CHOICE_PAD_X);
  const between = px(CHOICE_GAP);
  const reserved = sms.numbers ? NUMBER_EM : 0;
  const widest = Math.max(
    0,
    ...labels.flatMap((label) => label.split(/\s+/).filter(Boolean)).map(emsOf),
  );
  const height = (columns: number, k: number) => {
    const column = Math.min(COLUMN_MAX, (width - (columns - 1) * between * k) / columns);
    const line = (column - 2 * padX * k - CHOICE_BORDER) / (font * k) - reserved;
    /* ⚠️ UN MOT PLUS LARGE QUE SA CASE EN SORT : la grille ne l'élargit pas
       (`minmax(0, …)`). Sur une fenêtre étroite, trois colonnes laissaient « Réponse »
       dépasser du bouton — une grille qui ne tient pas, quelle que soit sa hauteur. */
    if (widest > line) return Infinity;
    return rowsOf(
      labels,
      columns,
      line,
      (lines) => (2 * padY + lines * font * LINE) * k + CHOICE_BORDER,
      between * k,
    );
  };
  const options: Fitting[] = [];
  for (let columns = 1; columns <= MAX_COLUMNS; columns++) {
    options.push(fitting(columns, height, space));
  }
  return strip(best(options));
}

type Fitting = RoomLayout & {
  /** Ce qui dépasse encore au plancher, en `vmin` ; 0 si la liste tient. */
  overflow: number;
};

/** Le plus grand facteur qui fait tenir la liste sur `columns` colonnes, par pas de 0,02. */
function fitting(
  columns: number,
  height: (columns: number, scale: number) => number,
  space: number,
): Fitting {
  for (let percent = 100; percent >= MIN_SCALE * 100; percent -= 2) {
    const scale = percent / 100;
    if (height(columns, scale) <= space) return { columns, scale, overflow: 0 };
  }
  return { columns, scale: MIN_SCALE, overflow: height(columns, MIN_SCALE) - space };
}

/**
 * Le texte le plus grand ; au plancher, ce qui déborde le moins ; à égalité, le moins
 * de colonnes — une liste se lit mieux que des cases.
 */
function best(options: Fitting[]): Fitting {
  return options.reduce((kept, option) =>
    option.scale > kept.scale ||
    (option.scale === kept.scale && option.overflow < kept.overflow)
      ? option
      : kept,
  );
}

const strip = ({ columns, scale }: Fitting): RoomLayout => ({ columns, scale });

/**
 * LA HAUTEUR DES RANGÉES, en pixels. Rangées dans l'ordre de la grille, ligne par
 * ligne : une rangée prend la hauteur de sa plus longue réponse.
 */
function rowsOf(
  labels: string[],
  columns: number,
  /** La largeur d'une ligne de libellé, en `em`. */
  line: number,
  rowHeight: (lines: number) => number,
  gap: number,
): number {
  let height = 0;
  for (let start = 0; start < labels.length; start += columns) {
    const lines = Math.max(
      ...labels.slice(start, start + columns).map((label) => linesOf(label, line)),
    );
    height += (start ? gap : 0) + rowHeight(lines);
  }
  return height;
}

/**
 * LE NOMBRE DE LIGNES D'UN TEXTE sur `width` `em`, coupé aux espaces comme le fait le
 * navigateur. Un mot plus long que la ligne la dépasse sans se couper : il en prend une.
 */
export function linesOf(text: string, width: number): number {
  let lines = 1;
  let used = 0;
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const size = emsOf(word);
    if (used && used + SPACE + size <= width) {
      used += SPACE + size;
    } else {
      if (used) lines += 1;
      used = size;
    }
  }
  return lines;
}

/**
 * ⚠️ LA LARGEUR D'UN MOT, LETTRE PAR LETTRE, et pas « tant de caractères ». Une moyenne
 * mesurée sur une phrase (0,49 `em`) tenait pour « Plutôt d'accord avec la proposition »,
 * pleine de `l`, de `t` et d'apostrophes, et sous-estimait « Réponse 10 » (0,59 `em`),
 * capitale et chiffres compris : le libellé passait sur deux lignes que la règle n'avait
 * pas prévues. Quatre familles, mesurées dans la police du produit et arrondies
 * au-dessus pour la graisse des réponses (600).
 */
const SPACE = 0.33;
const NARROW = /[ijlftrI.,;:'’!|()\-]/;
const WIDE = /[mwMW]/;
const CAPITAL = /[A-Z0-9\u00C0-\u00DE]/;

function emsOf(word: string): number {
  let size = 0;
  for (const char of word) {
    size += WIDE.test(char) ? 0.92 : NARROW.test(char) ? 0.33 : CAPITAL.test(char) ? 0.74 : 0.62;
  }
  return size;
}

/**
 * ═══ LE DERNIER RECOURS : RÉDUIRE CE QUI NE TIENT PAS ═══
 *
 * `roomLayout` estime ce qu'il connaît — les réponses, les barres — et le range pour
 * tenir. Mais tout ne s'estime pas : le podium, les tuiles de la bonne réponse, le
 * verdict, un nuage qui se remplit, des noms d'entreprise sur trois lignes. Vu sur
 * TDE-28-2026, dans une fenêtre de portable sous un bandeau : le podium sortait par le
 * bas, et le total avec lui. Un mur ne défile pas.
 *
 * Ici on MESURE (voir `LiveScene`, `FitToRoom`) : la hauteur naturelle du module, la
 * place qu'il a. S'il dépasse, il est réduit d'un bloc, proportions gardées ; s'il
 * tient, rien ne change. Jamais sous `MIN_SCALE` : en dessous, un texte projeté ne se
 * lit plus, et mieux vaut un bas coupé qu'un écran entier illisible.
 *
 * ⚠️ UN FACTEUR À UN CENTIÈME PRÈS. La mesure bouge d'un pixel d'une relecture à
 * l'autre ; sans arrondi, le module tremblerait à chaque vote reçu.
 */
export function fitScale(natural: number, available: number): number {
  if (!(natural > 0) || !(available > 0) || natural <= available) return 1;
  return Math.max(MIN_SCALE, Math.floor((available / natural) * 100) / 100);
}
